import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, Modal } from './shared';

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Cairo' });
const autoSerial = () => `NOSN-${Math.random().toString(16).slice(2, 8).toUpperCase()}`;

/** New repair ticket: pick a client → one of its devices (or add a new device) → fault. clientId preselects the client. */
export default function NewTicketModal({ clientId, onClose }) {
  const [clients, setClients] = useState([]);
  const [devices, setDevices] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [q, setQ] = useState('');
  const [f, setF] = useState({ client_id: clientId || '', device_id: '', fault_description: '', received_at: today(), main_engineer_id: '', notes: '' });
  const [newDev, setNewDev] = useState(null); // { name, serial, alias }
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const nav = useNavigate();
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    api.getClients().then(setClients).catch(() => {});
    fetch('/api/finance/engineers?brief=1').then(r => (r.ok ? r.json() : [])).then(setEngineers).catch(() => {});
  }, []);
  useEffect(() => {
    if (!f.client_id) { setDevices([]); return; }
    api.getDevices('', f.client_id).then(d => {
      setDevices(d);
      setF(x => ({ ...x, device_id: d.length === 1 ? d[0].id : '' }));
      setNewDev(d.length ? null : { name: '', serial: '', alias: '' });
    }).catch(() => {});
  }, [f.client_id]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? clients.filter(c => [c.name, c.phone, c.client_code].some(v => (v || '').toLowerCase().includes(s))) : clients;
  }, [clients, q]);
  const client = clients.find(c => c.id === f.client_id);

  async function save() {
    if (!f.client_id) return toast('اختار العميل', 'error');
    if (!newDev && !f.device_id) return toast('اختار الجهاز أو ضيف جهاز جديد', 'error');
    if (newDev && !newDev.name.trim()) return toast('اسم الجهاز مطلوب', 'error');
    setBusy(true);
    try {
      let deviceId = f.device_id;
      if (newDev) {
        const d = await api.createDevice({ name: newDev.name.trim(), serial: newDev.serial.trim() || autoSerial(), alias: newDev.alias.trim() || undefined, client_id: f.client_id });
        deviceId = d.id;
      }
      const t = await api.createTicket({ device_id: deviceId, fault_description: f.fault_description || null, notes: f.notes || null, received_at: f.received_at || null });
      if (f.main_engineer_id) {
        await fetch(`/api/finance/tickets/${t.id}/engineers`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ main_engineer_id: f.main_engineer_id }) }).catch(() => {});
      }
      toast('التيكت اتفتح ✓');
      nav(`/tickets/${t.id}`);
    } catch (e) { toast(e.message, 'error'); setBusy(false); }
  }

  return (
    <Modal width={620} title="🎫 تيكت جديد" onClose={onClose} footer={<>
      <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? '...' : 'افتح التيكت'}</button></>}>
      <div dir="rtl">
        <div className="field"><label>العميل *</label>
          {client && clientId ? <div className="fw-600">{client.name} <span className="mono text-xs text-dim">{client.client_code}</span></div> : <>
            <input className="input mb-8" placeholder="🔍 دوّر بالاسم / التليفون / الكود" value={q} onChange={e => setQ(e.target.value)} autoFocus={!clientId} />
            <select className="select" size={Math.min(6, Math.max(3, shown.length))} value={f.client_id} onChange={set('client_id')} style={{ height: 'auto' }}>
              {shown.map(c => <option key={c.id} value={c.id}>{c.name}{c.area ? ` — ${c.area}` : ''}{c.client_code ? `  (${c.client_code})` : ''}</option>)}
            </select>
          </>}
        </div>

        {f.client_id && <div className="field"><label>الجهاز *</label>
          {!newDev ? <>
            <select className="select" value={f.device_id} onChange={set('device_id')}>
              <option value="">— اختار جهاز ({devices.length}) —</option>
              {devices.map(d => <option key={d.id} value={d.id}>{d.name}{d.alias ? ` · ${d.alias}` : ''} — {d.serial}{d.last_status && d.last_status !== 'delivered' ? ' ⚠ عليه تيكت مفتوح' : ''}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm mt-8" onClick={() => setNewDev({ name: '', serial: '', alias: '' })}>+ جهاز جديد للعميل ده</button>
          </> : <div style={{ background: 'var(--bg3)', padding: 10, borderRadius: 8 }}>
            <div className="grid-3">
              <div className="field" style={{ margin: 0 }}><label>اسم الجهاز *</label><input className="input" value={newDev.name} onChange={e => setNewDev(x => ({ ...x, name: e.target.value }))} placeholder="Cryo 21 / Shockwave ..." autoFocus /></div>
              <div className="field" style={{ margin: 0 }}><label>السيريال</label><input className="input" dir="ltr" value={newDev.serial} onChange={e => setNewDev(x => ({ ...x, serial: e.target.value }))} placeholder="لو مش معروف هيتعمل تلقائي" /></div>
              <div className="field" style={{ margin: 0 }}><label>اسم مميز</label><input className="input" value={newDev.alias} onChange={e => setNewDev(x => ({ ...x, alias: e.target.value }))} placeholder="جهاز أوضة 2" /></div>
            </div>
            {devices.length > 0 && <button className="btn btn-ghost btn-sm mt-8" onClick={() => setNewDev(null)}>← اختار من أجهزة العميل</button>}
          </div>}
        </div>}

        <div className="field"><label>العطل</label>
          <textarea className="input" rows={2} value={f.fault_description} onChange={set('fault_description')} placeholder="مثلاً: الكرايو مش بيشفط" /></div>
        <div className="grid-2">
          <div className="field"><label>تاريخ الاستلام</label><input className="input" type="date" value={f.received_at} onChange={set('received_at')} /></div>
          {engineers.length > 0 && <div className="field"><label>المهندس الأساسي</label>
            <select className="select" value={f.main_engineer_id} onChange={set('main_engineer_id')}>
              <option value="">— بعدين —</option>
              {engineers.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select></div>}
        </div>
        <div className="field"><label>ملاحظات</label><input className="input" value={f.notes} onChange={set('notes')} /></div>
      </div>
    </Modal>
  );
}
